/* eslint-disable @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-argument */
import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { ActivatedRoute } from '@angular/router'
import { filter, finalize, first, map, switchMap, takeUntil, tap } from 'rxjs/operators'
import { forkJoin } from 'rxjs'
import dayjs from 'dayjs'

import { VehiclesService } from '@app/modules/client/vehicles/vehicles.service'
import { AuthService } from '@app/modules/auth/auth.service'
import { ClientsService } from '@app/modules/admin/clients/clients.service'
import { PermissionsService } from '@shared/services/permissions.service'
import { DevicesService } from '@shared/modules/devices/devices.service'
import { TrackedUnsavedForm, UnsavedFormTracker } from '@core/services/unsaved-form-tracker.service'
import { DialogService } from '@shared/dialogs/dialog.service'
import { TranslateService } from '@ngx-translate/core'
import { DestroyService } from '@core/services/destroy.service'

import { takeFormSnapshot } from '@core/helpers/take-form-snapshot'
import { handleFormErrors } from '@core/helpers/handle-form-errors'
import { LinValidators } from '@core/helpers/validators'
import { VehicleGroupInList } from '@shared/models/vehicle-group.model'
import { DepotInList } from '@shared/models/depot.model'
import { User, UserInList } from '@shared/models/user.model'
import { Vehicle, VehicleModelType, VehicleStatus } from '@shared/models/vehicle.model'
import { Driver } from '@shared/models/driver.model'
import { RoleType } from '@core/types/role.type'
import { NoteHistory } from '@shared/models/note.model'
import { PermissionSourceType, PermissionsSource } from '@core/types/permissions.source'
import { InstallDateCorrectionDialogComponent } from '@shared/dialogs/install-date-correction-dialog/install-date-correction-dialog.component'
import { DeviceInstallation } from '@shared/models/device.model'

type DriverTruncated = Pick<Driver, 'id' | 'fullName'>

@Component({
  selector: 'lin-edit-vehicle',
  templateUrl: './edit-vehicle.component.html',
  styleUrls: ['./edit-vehicle.component.scss'],
  providers: [DestroyService],
  standalone: false,
})
export class EditVehicleComponent implements OnInit, OnDestroy, TrackedUnsavedForm {
  vehicleForm: FormGroup
  isSubmitting = false
  user: User
  drivers: DriverTruncated[]
  isAutocompleteListLoading = false
  isFormChanged: ReturnType<typeof takeFormSnapshot>
  vehicleTypesList: VehicleModelType[]
  clientNotesHistory: NoteHistory[]
  adminNotesHistory: NoteHistory[]

  @Input() depotList: DepotInList[]
  @Input() vehicle: Vehicle
  @Input() vehicleGroupList: VehicleGroupInList[]
  @Input() deviceInstallation: DeviceInstallation

  readonly PermissionsSource = PermissionsSource

  get isEditMode(): boolean {
    return this.hasPermission(PermissionsSource['FLEET.VEHICLE_LIST.EDIT'])
  }

  get driverStartDateError(): string {
    return <string>this.vehicleForm?.get('driverStartDate')?.errors?.server
  }

  get currentYear(): number {
    return dayjs().tz().year()
  }

  get canCorrectInstallationDate(): boolean {
    const hasPermission = this.hasPermission(PermissionsSource['DEVICE.DEVICES_LIST.INSTALL'])

    return this.user.isAdminOrReseller() && hasPermission
  }

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly route: ActivatedRoute,
    private readonly vehiclesService: VehiclesService,
    private readonly authService: AuthService,
    private readonly clientsService: ClientsService,
    private readonly permissionsService: PermissionsService,
    private readonly devicesService: DevicesService,
    private readonly dialog: DialogService,
    private readonly translateService: TranslateService,
    private readonly unsavedFormTrackerService: UnsavedFormTracker,
    private readonly destroyService: DestroyService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.unsavedFormTrackerService.track(this)
    this.initForm()
    this.authService.currentUser$
      .pipe(
        first(),
        tap((user: User) => (this.user = user)),
        switchMap(() => this.vehiclesService.getVehiclesTypes()),
        takeUntil(this.destroyService),
      )
      .subscribe((vehicleTypesList: VehicleModelType[]) => (this.vehicleTypesList = vehicleTypesList))

    if (!this.vehicle) {
      this.getData()
    } else {
      this.setFormValues()
      this.getNotes()
    }
  }

  private getData(): void {
    const { id } = this.route.parent.snapshot.params

    const depotList$ = this.vehiclesService.getDepotList()
    const vehicleGroupList$ = this.vehiclesService.getVehicleGroupList()
    const vehicle$ = this.vehiclesService.getVehicle(id, ['driverHistory'])

    forkJoin([depotList$, vehicle$, vehicleGroupList$]).subscribe(([depots, vehicle, vehicleGroups]) => {
      this.vehicle = vehicle
      this.depotList = depots
      this.vehicleGroupList = vehicleGroups

      this.getNotes()
      this.setFormValues()
    })
  }

  hasPermission(permissionType: PermissionSourceType): boolean {
    return this.permissionsService.hasPermissionSync(permissionType)
  }

  private initForm(): void {
    this.vehicleForm = this.formBuilder.group({
      vehicleId: [{ value: '', disabled: true }],
      defaultLabel: ['', Validators.required],
      regNo: ['', Validators.required],
      type: '',
      driver: '',
      driverStartDate: '',
      make: '',
      makeModel: '',
      regDate: ['', LinValidators.yearValidator()],
      depot: '',
      groups: '',
      isUnavailable: '',
      unavailableMessage: '',
      clientNote: '',
    })

    if (!this.isEditMode) {
      this.vehicleForm.disable()
    }

    this.handleDriverValueChanges()
    this.isFormChanged = takeFormSnapshot(this.vehicleForm)
  }

  private handleDriverValueChanges(): void {
    const toDriverTruncated = ({ id, fullName }) => ({ id, fullName }) as DriverTruncated
    this.vehicleForm.controls.driver.valueChanges
      .pipe(
        tap(() => (this.isAutocompleteListLoading = true)),
        switchMap((autocompleteClientName: string) =>
          this.clientsService.getClientUserList(this.vehicle.team?.clientId, { fullName: autocompleteClientName, role: RoleType.Driver }),
        ),
        map((drivers: UserInList[]) => drivers.map(toDriverTruncated)),
        tap(() => (this.isAutocompleteListLoading = false)),
        takeUntil(this.destroyService),
      )
      .subscribe((drivers: DriverTruncated[]) => (this.drivers = drivers))
  }

  private setFormValues(): void {
    const { vehicle } = this
    const toId = ({ id }: VehicleGroupInList) => id

    this.vehicleForm.setValue({
      vehicleId: vehicle.id,
      defaultLabel: vehicle.defaultLabel,
      regNo: vehicle.regNo,
      type: vehicle.typeId ?? null,
      make: vehicle.make,
      makeModel: vehicle.makeModel,
      regDate: dayjs(vehicle.regDate).year(),
      depot: (vehicle.depot && vehicle.depot.id) || null,
      groups: vehicle.groups?.map(toId) ?? [],
      isUnavailable: (vehicle.status && vehicle.status === VehicleStatus.Unavailable) || false,
      unavailableMessage: vehicle.unavailableMessage || '',
      driver: vehicle.driver || '',
      driverStartDate: vehicle.driverHistory ? dayjs(vehicle.driverHistory.startDate).tz() : '',
      clientNote: vehicle.clientNote || '',
    })

    !this.isEditMode && this.vehicleForm.disable()
    this.isFormChanged = takeFormSnapshot(this.vehicleForm)
  }

  saveData(): void {
    this.isSubmitting = true
    const saveData: Partial<Vehicle> = this.prepareDataForSave()

    const editSubscribeNext = (): void => {
      this.isFormChanged = takeFormSnapshot(this.vehicleForm)
      this.vehicleForm.markAsPristine()
    }

    const editSubscribeError = (errors): void => {
      handleFormErrors(this.vehicleForm)(errors)
      const isStartError = errors?.error?.errors?.[0]?.detail?.startDate

      if (!isStartError) return

      this.vehicleForm.get('driverStartDate').setErrors({ server: Object.values(isStartError).join(' ') })
      this.cdr.detectChanges()
    }

    this.vehiclesService
      .editVehicle(this.vehicle.id, saveData)
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe(editSubscribeNext, editSubscribeError)
  }

  private prepareDataForSave(): Partial<Vehicle> {
    const formData = this.vehicleForm.value

    const data = {
      defaultLabel: formData.defaultLabel,
      regNo: formData.regNo,
      typeId: formData.type,
      make: formData.make,
      makeModel: formData.makeModel,
      regDate: formData.regDate && `${<number>formData.regDate}-01-02`,
      driverStartDate: formData.driverStartDate ? dayjs(formData.driverStartDate).tz(undefined, true).format() : null,
      isUnavailable: formData.isUnavailable,
      unavailableMessage: formData.unavailableMessage,
      clientNote: formData.clientNote,
      teamId: this.vehicle.team.id,
    }

    data['groupIds'] = formData.groups
    data['depotId'] = formData.depot
    data['driverId'] = formData.driver.id ?? null

    return data
  }

  displayDriverName(driver?: DriverTruncated): string | null {
    return (driver && driver.fullName) || null
  }

  getNotes(): void {
    this.vehiclesService
      .getVehicleNotes(this.vehicle.id, 'client')
      .pipe(
        tap((notes) => (this.clientNotesHistory = notes)),
        filter(() => this.user.isAdmin()),
        switchMap(() => this.vehiclesService.getVehicleNotes(this.vehicle.id, 'admin')),
        takeUntil(this.destroyService),
      )
      .subscribe((notes: NoteHistory[]) => {
        this.adminNotesHistory = notes
      })
  }

  addNote(noteType: 'admin' | 'client', noteValue: string): void {
    this.vehiclesService.addClientNote(this.vehicle.id, noteValue, noteType).subscribe(() => {
      this.getNotes()
    })
  }

  deleteNote(id: number): void {
    this.clientsService
      .deleteClientNote(id)
      .pipe(takeUntil(this.destroyService))
      .subscribe(() => {
        this.getNotes()
      })
  }

  openInstallationTimeCorrectionDialog(): void {
    const title = <string>this.translateService.instant('BUTTONS.CORRECT_INSTALLATION_TIME')

    this.dialog
      .open(InstallDateCorrectionDialogComponent, title, this.deviceInstallation)
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap(() => this.devicesService.getDevice(this.deviceInstallation.deviceId)),
        takeUntil(this.destroyService),
      )
      .subscribe((device) => {
        this.deviceInstallation = device.deviceInstallation
      })
  }

  displayVehicleType(type?: VehicleModelType): string | null {
    return (type && type.name) || null
  }

  handleVehicleTypeSelect(typeId: number): void {
    this.vehicleForm.get('type').setValue(typeId)
    this.vehicleForm.markAsDirty()
  }

  ngOnDestroy(): void {
    this.unsavedFormTrackerService.discard(this)
  }
}
